/**
 * @module app
 */

import { autoinject, Aurelia, TaskQueue, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration, RouteConfig, NavigationInstruction, AppRouter, Next, Redirect, RedirectToRoute } from 'aurelia-router';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { NavigationService } from 'app/core/services/navigation-service';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { Api } from 'app/shared/lib/api/api';
import { CheckinViewModel } from 'app/core/pages/checkin';
import { AppSession } from 'app/shared/lib/session/session';
import { EssTenantSelectionModalCustomElement } from 'app/core/elements/ess-tenant-selection-modal';
import { LocationContext } from 'app/shared/lib/location-context';
import { debounce } from 'app/shared/lib/decorators';
import { persist } from 'app/shared/lib/persist-decorator';
import { HelpService } from 'app/shared/services/help-service';
import { TitleChangeEvent, MenuRefreshEvent, StoreFocusEvent, EnableReturnFocusEvent, ReturnFocusEvent, AppRefocusEvent, SetFocusEvent } from 'app/shared/lib/events';
import { IMenuItem } from 'app/shared/services/navigation-service';
import { AccountContext } from 'app/core/lib/account-context';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { SessionChangedEvent, SessionExpiringEvent } from 'app/shared/lib/session/session-events';
import { IAppRouteConfig, IRouteConfigSettings } from 'app/shared/config/router-types';
import { LogoutContext } from 'app/shared/lib/logout-context';
import { I18N } from 'aurelia-i18n';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { PassphraseService } from 'app/shared/services/passphrase-service';
import { LockoutState } from 'app/shared/elements/layout/ess-lockout'
import { IDialog } from 'app/shared/elements/ess-dialog';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { Toaster } from 'app/shared/lib/toaster';
import { focusFirst, IReturnFocus, getFocusSelector, returnFocus } from 'app/shared/lib/focus';
import { deepClone } from 'app/shared/lib/utilities';
import { CXonePatch } from 'app/shared/lib/external/cxone-patch';
import './bundle-hints';
import '../scss/styles.scss';

@autoinject()
export class AppEntry {

    ////****************** Fields and Properties ************************************////
    protected pageTestName: string = '';
    protected titles: string[] = [];
    protected router!: Router;
    protected state = new State();
    protected enableReturnFocus: boolean = false;
    protected routerViewFocusElements: Element[] = [];
    protected focusHistory: IReturnFocus[] = [];
    protected focusReturnPointLookup: { [id: string]: IReturnFocus; } = {};
    protected menuItems: IMenuItem[] = [];
    protected displayBottomMenu: boolean = false;

    protected currentHref: string;
    protected helpUrl: string = '';
    protected lockoutState: LockoutState = LockoutState.Unlocked;
    protected lockedState: LockoutState = LockoutState.Locked;
    protected isSessionExpiringModalOpen = false;

    @persist({ initialValue: true })
    protected sideNavExpanded!: boolean;

    private eventSubscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $tenantModal!: EssTenantSelectionModalCustomElement;
    protected $sessionModal!: IDialog;

    constructor(
        private readonly toaster: Toaster,
        protected readonly session: AppSession,
        protected readonly i18n: I18N,
        private readonly app: Aurelia,
        private readonly api: Api,
        private readonly cxonePatch: CXonePatch,
        private readonly env: EnvironmentService,
        private readonly navigationService: NavigationService,
        private readonly events: EventAggregator,
        private readonly logger: AppLogger,
        private readonly location: LocationContext,
        private readonly logOutContext: LogoutContext,
        private readonly accountContext: AccountContext,
        private readonly helpService: HelpService,
        private readonly taskQueue: TaskQueue,
        protected readonly transitionalManager: TransitionalManager,
        passphraseService: PassphraseService) {
        this.logger.source = this;

        // TODO: better to get current route name and match by routed
        //let path = window.location.href; //window.location.pathname + window.location.hash;
        //if(!path.startsWith('/')) {
        //    path = `/${path}`;
        //}
        //if(path.endsWith('/')) {
        //    path = path.substr(0, path.length - 1);
        //}
        this.currentHref = window.location.href; //path;
        if(this.env.application.code === 'Portal.Providers' && this.env.config.externalSystems.cxone?.chat.enabled) this.displayBottomMenu = true;

        // TODO [Matt] This is defined in CSS but not js. Unify.
        // INFO [Matt] Since the menu fills the screen on small screens, force a close.
        if(screen.width < 600) {
            this.sideNavExpanded = false;
        }
        this.syncSideNavVariable();

        passphraseService.registerPassphrase('care bear stare', () => {
            document.body.classList.toggle('ess-illuminate');
        });
    }

    protected activate(): void {
        this.loadMenu();
    }

    protected attached(): void {
        this.session.startTimer();
        this.eventSubscriptions.push(
            this.events.subscribe(SessionChangedEvent,
                (e: SessionChangedEvent) => {
                    if(e.hasUserLoggedOut) {
                        let messageKey = 'ui:messages.loggedOut';
                        switch(e.key) {
                            case 'logOut':
                                messageKey = 'ui:messages.logOut';
                                break;
                            case 'expire':
                                messageKey = 'ui:messages.sessionExpired';
                                break;
                            case 'authFailed':
                                messageKey = 'ui:messages.lockedOutNeedCredentials';
                                this.toaster.info().message(this.i18n.tr(messageKey)).duration('indefinite').show();
                                break;
                        }
                        this.logOut(false, messageKey);
                    }
                    else if(e.hasUserContextChanged) {
                        this.accountContext.clear();
                        // user context changed, reload menu
                        this.loadMenu();
                        this.router.navigate('/home');
                    }
                })
        );

        this.eventSubscriptions.push(this.events.subscribe(SessionExpiringEvent,
            (e: SessionExpiringEvent) => this.handleSessionExpiring(e)
        ));

        this.eventSubscriptions.push(
            this.events.subscribe(MenuRefreshEvent, () => {
                this.loadMenu();
            })
        );

        this.eventSubscriptions.push(
            this.events.subscribe(StoreFocusEvent, () => {
                this.storeCurrentFocus();
            })
        );

        this.eventSubscriptions.push(
            this.events.subscribe(EnableReturnFocusEvent, () => {
                this.enableReturnFocus = true;
            })
        );

        this.eventSubscriptions.push(
            this.events.subscribe(ReturnFocusEvent, () => {
                this.returnToLastFocus();
            })
        );

        this.eventSubscriptions.push(
            this.events.subscribe(SetFocusEvent, (event: SetFocusEvent) => {
                this.setFocus(event.focalElement);
            })
        );

        this.eventSubscriptions.push(
            this.events.subscribe(AppRefocusEvent, () => {
                if(!this.enableReturnFocus) this.checkAndApplyFocus();
            })
        );

        // configure transitional
        if(this.env.config.essetteTransitional.enabled) {
            this.transitionalManager.listenForPostMessages();
        }

        // load cxone if enabled
        if(this.env.config.externalSystems?.cxone?.chat?.enabled) {
            this.cxonePatch.loadCXOne();
        }
    }

    protected detached(): void {
        this.eventSubscriptions.forEach(s => s.dispose());
        this.eventSubscriptions = [];
        this.session.stopTimer();
        // unload cxone if enabled
        if(this.env.config.externalSystems?.cxone?.chat?.enabled) {
            this.cxonePatch.endSessionAndReload();
        }
    }

    protected handleSessionExpiring = (sessionEvent: SessionExpiringEvent) => {
        if(sessionEvent.data.isAlerted && sessionEvent.data.seconds && this.$sessionModal?.state === OpenFlags.Closed) {
            switch(sessionEvent.data.expiringType) {
                case 'critical':
                    this.openSessionExpirationModal();
                    break;
                case 'warning':
                    this.toaster.warn().message(this.i18n.tr('ui:messages.warnings.sessionExpiresIn', { count: (sessionEvent.data.seconds / 60), what: 'minutes' })).duration('default').show();
                    break;
                default:
                    break;
            }
        }
    };

    protected openSessionExpirationModal() {
        this.isSessionExpiringModalOpen = true;
        this.$sessionModal.open();
    }

    protected closeSessionExpirationModal = () => {
        this.$sessionModal.close();
        this.isSessionExpiringModalOpen = false;
    }

    protected configureRouter(config: RouterConfiguration, router: AppRouter) {
        const path = this.location.path;
        if(path) {
            router.navigate(path);
        }

        config.title = this.env.config.description;
        config.addPipelineStep('postcomplete', PostCompleteStep)

        config.options.root = this.env.resolveUrl(this.env.baseUrl);

        const routes: RouteConfig[] = [];
        const notFound = PLATFORM.moduleName('app/shared/components/not-found');
        const noAccess = PLATFORM.moduleName('app/shared/components/no-access');

        const homeRoute = {
            // INFO [Nick] A lot of times, my initial visit uses #/login, so
            // when that happens I just want to be taken to my home page.
            // Otherwise, I get route not found.
            route: ['', 'home', 'login'],
            navigationStrategy: (instruction: NavigationInstruction) => {
                let { code, id, href } = this.env.user.homePage;
                let route = 'not-found';
                if(!code && id) {
                    if(this.env.config.essetteTransitional.enabled) {
                        // transitional page (no code, use id as :pageId)notifyTransitionalDisabled
                        const transitional = 'core.transitional';
                        const params = { pageId: id };
                        // QUESTION [Matt] Isn't this implied by essetteTransitional.enabled?
                        const config = routes.find((route: RouteConfig) => route.name === transitional);
                        if(config) {
                            route = router.generate(transitional, params).slice(2);
                        }
                    } else {
                        this.transitionalManager.notifyTransitionalDisabled();
                        route = 'no-access';
                    }
                }
                else if(href) {
                    // use href even if code is available in order to support child routers
                    route = href;
                }
                instruction.config.redirect = route;
            },
            settings: { bypassAuthentication: true }
        };

        routes.push(
            homeRoute,
            { route: 'no-access', name: 'no-access', moduleId: noAccess, title: 'ui.noAccess', settings: { bypassAuthentication: true } },
            { route: 'not-found', name: 'not-found', moduleId: notFound, title: 'ui.notFound', settings: { bypassAuthentication: true } }
        );

        config.addAuthorizeStep(AuthorizeStep);

        for(const module of this.env.getModules()) {
            for(const route of module.getRoutes()) {
                // assign module code as default help topic
                if(!route.settings) { route.settings = {}; }
                if(!route.settings.topic) {
                    route.settings.module = module.code;
                    route.settings.topic = `${module.code}.$root`;
                }

                // add route to list
                routes.push(route);
            }
        }
        config.map(routes);
        config.mapUnknownRoutes(notFound);

        const { events } = <any>router;
        events.subscribe('router:navigation:processing', () => {
            this.storeRouterViewElements();
        });
        events.subscribe('router:navigation:complete', () => {
            if(!router || !router.currentInstruction) { return; }
            const instructions = router.currentInstruction.getAllInstructions();
            this.pageTestName = router.currentInstruction.config.name || '';
            this.titles = instructions
                .map((instruction) => instruction.config.navModel && instruction.config.navModel.title || '')
                .filter((title) => title);

            // update current url in sidebar
            this.currentHref = window.location.href;

            // check for ?view=full and add full-view class to <body>
            if(router.currentInstruction?.queryParams?.view === 'full') {
                document.body.classList.add('full-view');
            }
            else {
                document.body.classList.remove('full-view');
            }
            this.configureHelp();
        });
        events.subscribe(TitleChangeEvent, (e: TitleChangeEvent) => {
            if(e.title) { this.titles = e.title; }
        });
        this.router = router;
    }

    protected storeRouterViewElements() {
        // NOTE: [EN] Storing a list of router-view elements to check for focusable elements.
        this.routerViewFocusElements = [];

        let tempRouterElement: Element | null | undefined = document.activeElement?.closest("router-view");
        do {
            if(tempRouterElement) {
                this.routerViewFocusElements.push(tempRouterElement);
                tempRouterElement = tempRouterElement?.parentElement?.closest("router-view");

            }
        } while(tempRouterElement)

        // NOTE: [EN] Reversing the array because we'll be using pop method to remove content to improve efficiency, during the process of focusing on closest to highest router-view.
        this.routerViewFocusElements = this.routerViewFocusElements.reverse();
    }

    // NOTE: [EN] If focus is not applied. Refocus the first focusable element from the closest router-view element.
    @debounce(250)
    protected checkAndApplyFocus() {
        if(document.activeElement?.tagName === "BODY" && this.routerViewFocusElements.length > 0) {

            while(document.activeElement.tagName === "BODY" && this.routerViewFocusElements.length > 0) {
                let routerViewElement = this.routerViewFocusElements.pop();
                if(routerViewElement) {
                    focusFirst(routerViewElement);
                }
            }
            this.routerViewFocusElements = [];
        }
    }

    protected storeCurrentFocus() {
        const selector = getFocusSelector();
        selector.location = deepClone(window.location);
        this.focusHistory.push(selector);
        if(this.focusHistory.length > window.history.length) {

            // Note: [EN] Arbitrarily limiting the number of stored focusHistory to have a max of the window.history. Removes older focusHistory items.
            this.focusHistory.splice(0, this.focusHistory.length - window.history.length);
        }
    }

    protected returnToLastFocus() {
        if(this.enableReturnFocus) {

            this.taskQueue.queueTask(() => {
                const lastFocus: IReturnFocus = this.focusHistory[this.focusHistory.length - 1];
                if(lastFocus && lastFocus.location && window.location.href === lastFocus.location.href) {
                    returnFocus(lastFocus);

                    this.focusHistory.pop();
                }

                // NOTE: Fallback for focusing when there is no focusHistory stored on prior page. Due to instances of manual url navigation/refresh.
                if(document.activeElement?.tagName === 'BODY' && this.routerViewFocusElements.length > 0) this.checkAndApplyFocus();
            })
            this.enableReturnFocus = false;
        }
    }

    protected setFocus(focalElement: HTMLElement) {
        focalElement.focus();
    }

    private syncSideNavVariable() {
        document.documentElement.style.setProperty(
            '--ess-sidenav-width',
            `var(--ess-sidenav-${this.sideNavExpanded ? 'expanded' : 'collapsed'}-width)`
        );
    }

    protected toggleMenu(expanded?: boolean) {
        if(expanded === undefined) {
            expanded = !this.sideNavExpanded;
        }
        this.sideNavExpanded = expanded;
        this.syncSideNavVariable();
    }

    private async loadMenu(): Promise<void> {
        try {
            this.logger.debug('Loading Sidebar Menu...');
            this.state.beginLoad();
            const response = await this.navigationService.loadMenu();
            this.menuItems = response.results;
        }
        catch(error) {
            this.logger.error('Unable to Load Sidebar Menu', error);
        }
        finally {
            this.state.endLoad();
        }
    }

    // Filter items from the menu (for example, when using feature flags)
    // ESS-4385: come up with a better long term solution
    protected filterMenu(items: IMenuItem[] | null | undefined, removeItemID: string, topLevelItem: boolean = false) {
        if(!items) return;

        if(topLevelItem) {
            this.menuItems = items.filter((item) => {
                return item.id !== removeItemID;
            });

            return;
        }

        items.forEach(item => {
            this.filterMenu(item.children, removeItemID);

            if(!item.children) return;
            item.children = item.children.filter((child) => {
                if(child.id === removeItemID)
                    this.logger.info(`Found Target of Removal: ${removeItemID}`)

                return child.id !== removeItemID;
            });
        });
    }

    protected userAction(action: string, messageKey: string): void {
        switch(action) {
            case 'log-out': this.logOut(true, messageKey); return;
            case 'check-in':
                this.$tenantModal.open().then((isComplete) => {
                    if(isComplete) {
                        const href = '/home'
                        const previousInstruction = this.router.currentInstruction;

                        // INFO [Matt] This is a hack. Deleting the current instruction is the
                        // most straightforward way to force a refresh on the current route.
                        // Other solutions include modifying the route config itself in order
                        // to force the activation strategy. I tried using an event handler on
                        // router:navigation:processing, and that works for a single navigation
                        // but does not work for a redirect, i.e. you can only modify the first
                        // navigation config.
                        // @ts-ignore
                        delete this.router.currentInstruction;
                        try {
                            this.logger.info(`Navigating to ${href}`, href);
                            this.router.navigate(href);
                        }
                        catch {
                            this.router.currentInstruction = previousInstruction;
                        }
                    }
                });
                return;
            //case 'my-account': this.router.navigate(`${this.transitional.route}/${5}`); return;
            case 'my-account': this.router.navigateToRoute('core.my-account'); return;
        }
    }

    protected help(): void {
        this.showHelp();
    }

    protected skipToMainContent(): boolean {
        const mainContent = document.getElementById('app-main');
        if(mainContent) { mainContent.focus(); }
        return true;
    }

    private async configureHelp(): Promise<void> {
        let topic: string = '';
        const config =
            (this.router.currentInstruction && this.router.currentInstruction.config || null) as
            (IAppRouteConfig | null);

        if(config) {
            const instructions: NavigationInstruction[] = this.router.currentInstruction.getAllInstructions();

            if(instructions) {
                topic = instructions
                    .filter(i => i.config.settings?.isVisible)
                    .map(i => i.config.settings?.topic || '').toString();
            }


            if(!topic && config.settings) {
                if(typeof config.settings.topic === 'string') {
                    topic = config.settings.topic || '';
                }
                else if(typeof config.settings.topic === 'function') {
                    topic = await config.settings.topic(this.router.currentInstruction);
                }
            }
            if(!topic && config.name) {
                topic = config.name;
            }
        }

        this.helpUrl = await this.helpService.resolveHelpUrl(topic);
    }

    private showHelp(): void {
        if(!this.helpUrl) { return; }

        window.open(this.helpUrl, this.env.config.help.windowTarget);
    }

    private logOut(sendToApi: boolean = true, messageKey: string = '') {
        CheckinViewModel.disableAutoLogIn();
        const isSignOnUser = this.env.user.signOnProvider;

        if(isSignOnUser) {
            this.router.navigate(`/signout?provider=${this.env.user.signOnProvider?.code}`, { replace: true, trigger: false });
            this.router.reset();
            this.app.setRoot('login');
        }
        else {
            if(sendToApi) {
                this.api.logOut();
            }
            this.logOutContext.message = (messageKey ? this.i18n.tr(messageKey) : '') || '';
            this.accountContext.clear();
            this.app.setRoot('login');
        }
    }
}

class PostCompleteStep {
    run(navigationInstruction: any, next: any) {
        window.scrollTo(0, 0);

        return next();
    }
}

export class SelectOneValueConverter {
    toView(arr: string[]) {
        const index = Math.floor(Math.random() * arr.length);
        return arr[index];
    }
}

@autoinject()
class AuthorizeStep {

    constructor(private readonly accountContext: AccountContext, private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    public async run(instruction: NavigationInstruction, next: Next): Promise<any> {
        await this.accountContext.load();

        const settings: IRouteConfigSettings = instruction.config.settings;
        if(typeof settings.action === 'function') {
            this.logger.debug(`Route Authorization: running action ${settings.action}`);
            if(settings.action()) {
                return next.cancel();
            }
        }

        const instructions = instruction.getAllInstructions();
        const authorized = instructions.every(instruction => {
            if(!instruction.config.name) { return false; }
            const settings: IRouteConfigSettings = instruction.config.settings;
            const hasAccess = this.accountContext.hasRouteAccess(instruction.config.name);
            this.logger.debug(`Route Authorization (${instruction.config.name}): Bypass: ${!!settings.bypassAuthentication}, hasAccess: ${hasAccess}`);
            return (!!settings.bypassAuthentication || hasAccess);
        });

        if(!authorized) {
            return next.cancel(new RedirectToRoute('no-access', { from: instruction.fragment }));
        }
        return next();
    }
}
